/**
 * Data transformers for ScoopTable
 * Transforms various data formats to AG-Grid compatible format
 */

/**
 * Check if data is in legacy format
 * Legacy format: { columns: [], rows: [[]] }
 * @param {Object} data - Data object
 * @returns {boolean}
 */
export const isLegacyFormat = (data) => {
    return (
        data && typeof data === 'object' && Array.isArray(data.columns) && Array.isArray(data.rows)
    )
}

/**
 * Transform legacy row data to object format
 * Legacy: [['value1', 'value2', ...]]
 * New: [{ col_0: 'value1', col_1: 'value2', ... }]
 * @param {Array} rows - Array of row arrays
 * @param {Array} columns - Column definitions (for field names)
 * @returns {Array} Array of row objects
 */
export const transformLegacyRows = (rows, columns) => {
    if (!rows || !Array.isArray(rows)) return []

    return rows.map((row, rowIndex) => {
        const rowObj = { _rowIndex: rowIndex }

        if (Array.isArray(row)) {
            row.forEach((cellValue, colIndex) => {
                rowObj[`col_${colIndex}`] = cellValue
            })
        }

        return rowObj
    })
}

/**
 * Transform legacy data format to new format
 * @param {Object} legacyData - { columns: [], rows: [[]] }
 * @returns {Object} { columns: [], rows: [{}] }
 */
export const transformLegacyData = (legacyData) => {
    if (!isLegacyFormat(legacyData)) {
        return { columns: [], rows: [] }
    }

    const { transformLegacyColumns } = require('./columnTransformers')

    return {
        columns: transformLegacyColumns(legacyData.columns),
        rows: transformLegacyRows(legacyData.rows, legacyData.columns),
    }
}

/**
 * Ensure rows have unique IDs for AG-Grid
 * @param {Array} rows - Array of row objects
 * @param {string} idField - Field to use as ID (default: 'id')
 * @returns {Array} Rows with guaranteed IDs
 */
export const ensureRowIds = (rows, idField = 'id') => {
    if (!rows || !Array.isArray(rows)) return []

    return rows.map((row, index) => {
        if (row[idField] !== undefined) {
            return row
        }
        return {
            ...row,
            [idField]: row._rowIndex ?? index,
        }
    })
}

/**
 * Extract row data from selection event
 * @param {Array} selectedNodes - AG-Grid selected nodes
 * @returns {Array} Array of row data objects
 */
export const extractSelectedRows = (selectedNodes) => {
    if (!selectedNodes || !Array.isArray(selectedNodes)) return []
    return selectedNodes.map((node) => node.data).filter(Boolean)
}

/**
 * Filter rows based on simple filter criteria
 * @param {Array} rows - Array of row objects
 * @param {Object} filters - Filter criteria { field: value }
 * @returns {Array} Filtered rows
 */
export const filterRows = (rows, filters) => {
    if (!rows || !filters || Object.keys(filters).length === 0) {
        return rows
    }

    return rows.filter((row) => {
        return Object.entries(filters).every(([field, filterValue]) => {
            const cellValue = row[field]

            if (filterValue === null || filterValue === undefined) {
                return true
            }

            if (typeof filterValue === 'string') {
                return String(cellValue).toLowerCase().includes(filterValue.toLowerCase())
            }

            return cellValue === filterValue
        })
    })
}

/**
 * Sort rows based on sort model
 * @param {Array} rows - Array of row objects
 * @param {Array} sortModel - [{ field: string, direction: 'asc'|'desc' }]
 * @returns {Array} Sorted rows
 */
export const sortRows = (rows, sortModel) => {
    if (!rows || !sortModel || sortModel.length === 0) {
        return rows
    }

    return [...rows].sort((a, b) => {
        for (const { field, direction } of sortModel) {
            const aVal = a[field]
            const bVal = b[field]

            let comparison = 0

            if (aVal === null || aVal === undefined) comparison = 1
            else if (bVal === null || bVal === undefined) comparison = -1
            else if (typeof aVal === 'string' && typeof bVal === 'string') {
                comparison = aVal.localeCompare(bVal)
            } else {
                comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
            }

            if (comparison !== 0) {
                return direction === 'desc' ? -comparison : comparison
            }
        }
        return 0
    })
}

/**
 * Paginate rows
 * @param {Array} rows - Array of row objects
 * @param {number} page - Current page (0-indexed)
 * @param {number} pageSize - Rows per page
 * @returns {Array} Paginated rows
 */
export const paginateRows = (rows, page, pageSize) => {
    if (!rows || pageSize <= 0) return rows

    const start = page * pageSize
    return rows.slice(start, start + pageSize)
}
